import type { FeatureCollection } from 'geojson';
import type { GeoJSONSource, LngLatBoundsLike, MapMouseEvent } from 'maplibre-gl';
import { useEffect } from 'react';
import { useMap } from './MapView';

export interface PointMarker {
  id: string;
  lat: number;
  lon: number;
  label: string;
  /** Short glyph shown inside the pin (e.g. "A", "B", "1"). */
  glyph?: string;
  color?: string;
}

/**
 * Labelled pins (origin/destination/stops). Fits bounds when `fit` is true and there are ≥2 pins.
 * `beforeId` inserts the layers under an existing layer (e.g. the vehicles) so they never cover it.
 * `id` namespaces the source/layers so several marker sets can share a map.
 */
export function MarkersLayer({ id = 'markers', markers, fit = false, beforeId }: { id?: string; markers: PointMarker[]; fit?: boolean; beforeId?: string }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const SRC = id;
    const before = beforeId && map.getLayer(beforeId) ? beforeId : undefined;
    const fc: FeatureCollection = {
      type: 'FeatureCollection',
      features: markers.map((m) => ({ type: 'Feature', properties: { id: m.id, label: m.label, glyph: m.glyph ?? '', color: m.color ?? '#0F3D26' }, geometry: { type: 'Point', coordinates: [m.lon, m.lat] } })),
    };
    const s = map.getSource(SRC) as GeoJSONSource | undefined;
    if (s) s.setData(fc);
    else map.addSource(SRC, { type: 'geojson', data: fc });
    if (!map.getLayer(`${SRC}-circle`)) {
      map.addLayer({ id: `${SRC}-circle`, type: 'circle', source: SRC, paint: { 'circle-radius': 11, 'circle-color': ['get', 'color'], 'circle-stroke-color': '#fff', 'circle-stroke-width': 2.5 } }, before);
      map.addLayer({ id: `${SRC}-glyph`, type: 'symbol', source: SRC, layout: { 'text-field': ['get', 'glyph'], 'text-size': 11, 'text-font': ['Noto Sans Bold'], 'text-allow-overlap': true }, paint: { 'text-color': '#fff' } }, before);
      map.addLayer({ id: `${SRC}-label`, type: 'symbol', source: SRC, layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-font': ['Noto Sans Regular'], 'text-offset': [0, 1.4], 'text-anchor': 'top', 'text-optional': true }, paint: { 'text-color': '#0F2A1C', 'text-halo-color': '#fff', 'text-halo-width': 1.4 } }, before);
    }
    if (fit && markers.length >= 2) {
      let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
      for (const m of markers) {
        minLat = Math.min(minLat, m.lat); maxLat = Math.max(maxLat, m.lat); minLon = Math.min(minLon, m.lon); maxLon = Math.max(maxLon, m.lon);
      }
      const b: LngLatBoundsLike = [[minLon, minLat], [maxLon, maxLat]];
      map.fitBounds(b, { padding: 70, duration: 400, maxZoom: 13 });
    } else if (fit && markers.length === 1) {
      map.easeTo({ center: [markers[0]!.lon, markers[0]!.lat], zoom: Math.max(map.getZoom(), 11), duration: 300 });
    }
  }, [map, id, markers, fit, beforeId]);
  return null;
}

/** Reports map clicks (used by the trip form to pick free points). */
export function ClickCapture({ onClick, active = true }: { onClick: (lat: number, lon: number) => void; active?: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !active) return;
    const h = (e: MapMouseEvent) => onClick(e.lngLat.lat, e.lngLat.lng);
    map.on('click', h);
    map.getCanvas().style.cursor = 'crosshair';
    return () => {
      map.off('click', h);
      map.getCanvas().style.cursor = '';
    };
  }, [map, onClick, active]);
  return null;
}
